import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import CSelect, {
  SelectDropdownGlobalStyle,
} from '@app/components/Common/CSelect';
import { useToken } from '@app/hooks';
import { Pagination } from '@/app/components/Pagination';
import { useCopyPortfolioStore } from '@app/hooks/copyTrading/copyPortfolioStore';
import { useMyPortfolio } from '@app/hooks/copyTrading/useMyPortfolio';
import { useMyProfile } from '@app/hooks/user/myprofile';
import { useTradingWallets } from '@app/hooks/user/useTradingWallets';
import TransferModal from '../QBIT/Assets/components/TransferModal';
import { CopyRegisterModal } from '../CopyTradePage/modal/CopyRegisterModal';
import { FollowPortfolioModal } from '../CopyTradePage/modal/FollowPortfolioModal';
import { EditFollowModal } from '../CopyTradePage/modal/EditFollowModal';
import { getMyCopyOrders } from '../../api/copyTrading';
import type {
  CopyPortfolio,
  CopyFollow,
  CopyOrder,
  OrderStatus,
} from '../../../types/copyTrading';
import BigNumber from 'bignumber.js';
import dayjs from 'dayjs';
import * as OrderHistoryStyles from './orderHistoryStyles';
import useMobileDimensions from '@app/hooks/useMobileDimensions';
import MobileCopyTradingPage from './MobileCopyTradingPage';
import DefaultProfileImage from './assets/profile.png';
import * as S from './styles';

// 심볼 필터 옵션 (DB exchange_pairs 기준)
const getSymbolOptions = (t: (key: string) => string) => [
  { value: 'all', label: t('allSymbols') },
  { value: 'BTC', label: 'BTC/USDT' },
  { value: 'ETH', label: 'ETH/USDT' },
  { value: 'XRP', label: 'XRP/USDT' },
  { value: 'BCH', label: 'BCH/USDT' },
  { value: 'LTC', label: 'LTC/USDT' },
  { value: 'EOS', label: 'EOS/USDT' },
  { value: 'XMR', label: 'XMR/USDT' },
  { value: 'ADA', label: 'ADA/USDT' },
  { value: 'AVAX', label: 'AVAX/USDT' },
  { value: 'DOGE', label: 'DOGE/USDT' },
  { value: 'DOT', label: 'DOT/USDT' },
  { value: 'UNI', label: 'UNI/USDT' },
  { value: 'LINK', label: 'LINK/USDT' },
  { value: 'SOL', label: 'SOL/USDT' },
  { value: 'TRX', label: 'TRX/USDT' },
  { value: '1000PEPE', label: '1000PEPE/USDT' },
  { value: 'SUI', label: 'SUI/USDT' },
];

// 상태 필터 옵션 (번역 키 사용)
const getStatusOptions = (t: (key: string) => string) => [
  { value: 'all', label: t('allStatus') },
  { value: '0', label: t('pending') },
  { value: '1', label: t('filled') },
  { value: '2', label: t('cancelled') },
];

// New Section Components
import {
  MyPortfolioSection,
  FollowersSection,
  EarningsSection,
} from './sections';

// Tab Type - Extended with leader-only tabs
type TabType =
  | 'discover'
  | 'following'
  | 'orders'
  | 'my-portfolio'
  | 'followers'
  | 'earnings';

const CopyTradingPage: React.FC = () => {
  const { isMobile } = useMobileDimensions();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useToken();
  const { hasPortfolio, portfolio: myPortfolioData } = useMyPortfolio();
  const { data: myProfile } = useMyProfile();

  const [activeTab, setActiveTab] = useState<TabType>('discover');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [openTransferModal, setOpenTransferModal] = useState(false);
  const [openRegisterModal, setOpenRegisterModal] = useState(false);
  const [openFollowModal, setOpenFollowModal] = useState(false);
  const [openEditFollowModal, setOpenEditFollowModal] = useState(false);
  const [selectedPortfolio, setSelectedPortfolio] =
    useState<CopyPortfolio | null>(null);
  const [selectedFollow, setSelectedFollow] = useState<CopyFollow | null>(null);
  const [selectedFollowPortfolio, setSelectedFollowPortfolio] =
    useState<CopyPortfolio | null>(null);

  const {
    portfolios,
    myFollows,
    fetchPortfolios,
    fetchMyFollows,
    isLoading,
    summary,
  } = useCopyPortfolioStore();

  const { data: walletData, getCopyAvailable } = useTradingWallets({
    enabled: activeTab === 'following' && !!token && !isMobile,
  });

  // Order History state
  const [orders, setOrders] = useState<CopyOrder[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [totalOrders, setTotalOrders] = useState(0);
  const [ordersPage, setOrdersPage] = useState(1);
  const [selectedSymbol, setSelectedSymbol] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');

  const fetchOrders = useCallback(async () => {
    if (!token) return;
    setOrdersLoading(true);

    const params = {
      page: ordersPage,
      limit: 20,
      symbol: selectedSymbol === 'all' ? undefined : selectedSymbol,
      status: selectedStatus === 'all' ? undefined : selectedStatus,
    };
    console.log('[CopyTradingPage] fetchOrders params:', params);

    try {
      const response = await getMyCopyOrders(params);
      console.log('[CopyTradingPage] fetchOrders response:', response);
      if (response.success) {
        setOrders(response.data?.items || []);
        setTotalOrders(response.data?.pagination?.total || 0);
      }
    } catch (error) {
      console.error('[CopyTradingPage] fetchOrders error:', error);
    } finally {
      setOrdersLoading(false);
    }
  }, [token, ordersPage, selectedSymbol, selectedStatus]);

  useEffect(() => {
    if (isMobile) return;

    if (activeTab === 'discover') {
      // ⭐ period='7d'로 요청하여 7일간 ROI/PnL 데이터 가져오기
      fetchPortfolios({ status: 1, page: 1, pageSize: 20, period: '7d' });
    } else if (activeTab === 'following' && token) {
      fetchMyFollows();
    } else if (activeTab === 'orders' && token) {
      fetchOrders();
    }
  }, [
    isMobile,
    activeTab,
    token,
    fetchPortfolios,
    fetchMyFollows,
    fetchOrders,
  ]);

  const filteredPortfolios = useMemo(() => {
    if (!portfolios) return [];
    return portfolios.filter((portfolio) => {
      const keyword = searchKeyword.toLowerCase();
      return (
        portfolio.leaderUsername?.toLowerCase().includes(keyword) ||
        portfolio.portfolioName?.toLowerCase().includes(keyword)
      );
    });
  }, [portfolios, searchKeyword]);

  const followingPortfolios = useMemo(() => {
    if (!myFollows || !portfolios) return [];
    const followingIds = myFollows
      .filter((f) => f.follow.status !== 3)
      .map((f) => f.follow.portfolioId);
    return portfolios.filter((p) => followingIds.includes(p.id));
  }, [myFollows, portfolios]);

  const displayedPortfolios =
    activeTab === 'following' ? followingPortfolios : filteredPortfolios;

  const handleCopyTrade = (portfolio: CopyPortfolio) => {
    setSelectedPortfolio(portfolio);
    setOpenFollowModal(true);
  };

  const isFollowing = (portfolioId: number) => {
    return (myFollows || []).some(
      (f) => f.follow.portfolioId === portfolioId && f.follow.status !== 3
    );
  };

  const isMyPortfolio = (portfolio: CopyPortfolio) => {
    // myPortfolioData의 id와 비교하여 자신의 포트폴리오인지 확인
    if (myPortfolioData?.id && portfolio.id === myPortfolioData.id) {
      return true;
    }
    // fallback: leaderUserId 비교
    if (myProfile?.seq && portfolio.leaderUserId === myProfile.seq) {
      return true;
    }
    return false;
  };

  // 이미지 URL 가져오기
  const getPortfolioImageUrl = (portfolio: CopyPortfolio): string | null => {
    const imageUrl =
      portfolio.imageUrl ||
      portfolio.metadata?.imageUrl ||
      portfolio.leaderProfileImage ||
      portfolio.leaderAvatar;
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http')) return imageUrl;
    return `${import.meta.env.VITE_APP_API_MEDIA_DEV_URL}/${imageUrl}`;
  };

  // 이니셜 추출
  const getInitials = (name: string): string => {
    if (!name) return '?';
    return name.charAt(0).toUpperCase();
  };

  // 차트 데이터 생성 (7일간 ROI 추이 시뮬레이션)
  const generateChartData = (finalROI: number) => {
    const data: Array<{ value: number }> = [];
    const baseValue = 0;
    const finalValue = finalROI;

    // 7일간의 데이터 포인트 생성
    for (let i = 0; i < 7; i++) {
      const progress = i / 6; // 0 to 1
      // 부드러운 곡선을 위한 easing 함수
      const easedProgress =
        progress < 0.5
          ? 2 * progress * progress
          : -1 + (4 - 2 * progress) * progress;
      const value = baseValue + (finalValue - baseValue) * easedProgress;
      data.push({ value: Math.max(0, value) });
    }

    return data;
  };

  if (isMobile) {
    return <MobileCopyTradingPage />;
  }

  return (
    <S.Container>
      <TransferModal
        open={openTransferModal}
        onClose={() => setOpenTransferModal(false)}
        defaultFrom="spotwallet"
        defaultTo="copywallet"
      />
      <CopyRegisterModal
        open={openRegisterModal}
        setOpen={setOpenRegisterModal}
      />
      <FollowPortfolioModal
        open={openFollowModal}
        setOpen={setOpenFollowModal}
        portfolio={selectedPortfolio}
      />
      <EditFollowModal
        open={openEditFollowModal}
        setOpen={setOpenEditFollowModal}
        follow={selectedFollow}
        portfolio={selectedFollowPortfolio}
      />

      {/* Hero Section */}
      <S.HeroSection>
        <S.HeroContent>
          <S.HeroTitle>
            <S.TitleCopy>{t('copy')}</S.TitleCopy>
            <S.TitleTrading>{t('trading')}</S.TitleTrading>
          </S.HeroTitle>
          <S.HeroDescription>{t('copyTradingDescription')}</S.HeroDescription>
          <S.HeroButtons>
            <S.TransferButton onClick={() => setOpenTransferModal(true)}>
              {t('transfer')}
            </S.TransferButton>
            {!hasPortfolio && (
              <S.BecomeLeaderButton onClick={() => setOpenRegisterModal(true)}>
                {t('becomeALeader')}
              </S.BecomeLeaderButton>
            )}
          </S.HeroButtons>
        </S.HeroContent>
        <S.HeroImage>
          <S.CharacterImage
            src="/assets/newHome/copy-trading/character.png"
            alt="Character"
          />
        </S.HeroImage>
      </S.HeroSection>

      {/* Tabs Section */}
      <S.TabsSection>
        <S.TabsContainer>
          <S.Tab
            $isActive={activeTab === 'discover'}
            onClick={() => setActiveTab('discover')}
          >
            {t('copyTradingPage.discover')}
          </S.Tab>
          <S.Tab
            $isActive={activeTab === 'following'}
            onClick={() => setActiveTab('following')}
          >
            {t('following')}
          </S.Tab>
          <S.Tab
            $isActive={activeTab === 'orders'}
            onClick={() => setActiveTab('orders')}
          >
            {t('orderHistory')}
          </S.Tab>
          {/* Leader-only tabs */}
          {hasPortfolio && (
            <>
              <S.Tab
                $isActive={activeTab === 'my-portfolio'}
                onClick={() => setActiveTab('my-portfolio')}
              >
                {t('myPortfolio')}
              </S.Tab>
              <S.Tab
                $isActive={activeTab === 'followers'}
                onClick={() => setActiveTab('followers')}
              >
                {t('myFollowers')}
              </S.Tab>
            </>
          )}
          {/* Earnings tab - for all users */}
          <S.Tab
            $isActive={activeTab === 'earnings'}
            onClick={() => setActiveTab('earnings')}
          >
            {t('earnings')}
          </S.Tab>
        </S.TabsContainer>
      </S.TabsSection>

      {/* Content Section */}
      {activeTab === 'following' ? (
        <S.FollowingSection>
          <S.DashboardSection>
            <S.DashboardTitle>{t('myFollowsDashboard')}</S.DashboardTitle>
            <S.StatsCard>
              <S.StatItem>
                <S.StatLabel>{t('copyTradingWalletBalance')}</S.StatLabel>
                <S.StatValue>
                  {walletData?.copy
                    ? BigNumber(walletData.copy.balance || 0).toFixed(2)
                    : '0.00'}
                  <S.StatUnit>USDT</S.StatUnit>
                </S.StatValue>
              </S.StatItem>
              <S.StatItem>
                <S.StatLabel>{t('totalAllocated')}</S.StatLabel>
                <S.StatValue>
                  {summary?.totalAllocated
                    ? BigNumber(summary.totalAllocated).toFixed(2)
                    : '0.00'}
                  <S.StatUnit>USDT</S.StatUnit>
                </S.StatValue>
              </S.StatItem>
              <S.StatItem>
                <S.StatLabel>{t('totalPnl')}</S.StatLabel>
                <S.StatValue $isPositive={true}>
                  {summary?.totalPnl
                    ? `${summary.totalPnl >= 0 ? '+' : ''}${BigNumber(summary.totalPnl).toFixed(2)}`
                    : '+0.00'}
                  <S.StatUnit>USDT</S.StatUnit>
                </S.StatValue>
              </S.StatItem>
              <S.StatItem>
                <S.StatLabel>{t('profitShared')}</S.StatLabel>
                <S.StatValue>
                  {summary?.totalProfitShared
                    ? BigNumber(summary.totalProfitShared).toFixed(2)
                    : '0.00'}
                  <S.StatUnit>USDT</S.StatUnit>
                </S.StatValue>
              </S.StatItem>
            </S.StatsCard>
          </S.DashboardSection>

          <S.PortfoliosSection>
            <S.PortfoliosTitle>{t('followingPortfolios')}</S.PortfoliosTitle>
            <OrderHistoryStyles.FollowingTableContainer>
              <Table
                columns={[
                  {
                    title: t('portfolio'),
                    dataIndex: ['portfolio', 'portfolioName'],
                    key: 'portfolio',
                    render: (text: string) => text || t('unknown'),
                  },
                  {
                    title: t('status'),
                    dataIndex: ['follow', 'status'],
                    key: 'status',
                    render: (status: number) => (
                      <OrderHistoryStyles.FollowingStatusTag $status={status}>
                        {status === 1
                          ? t('active')
                          : status === 2
                            ? t('paused')
                            : t('stopped')}
                      </OrderHistoryStyles.FollowingStatusTag>
                    ),
                  },
                  {
                    title: t('copyRatio'),
                    dataIndex: ['follow', 'copyRatio'],
                    key: 'copyRatio',
                    render: (ratio: number) =>
                      ratio
                        ? `${BigNumber(ratio).multipliedBy(100).toFixed(0)}%`
                        : '0%',
                  },
                  {
                    title: t('allocatedAmount'),
                    dataIndex: ['follow', 'allocatedAmount'],
                    key: 'allocatedAmount',
                    render: (amount: number) =>
                      `${amount ? BigNumber(amount).toFixed(2) : '0.00'} USDT`,
                  },
                  {
                    title: t('currentPnl'),
                    dataIndex: ['follow', 'currentPnl'],
                    key: 'currentPnl',
                    render: (pnl: number) => (
                      <OrderHistoryStyles.PnlText $positive={(pnl || 0) >= 0}>
                        {pnl
                          ? `${pnl >= 0 ? '+' : ''}${BigNumber(pnl).toFixed(2)}`
                          : '+0.00'}{' '}
                        USDT
                      </OrderHistoryStyles.PnlText>
                    ),
                  },
                  {
                    title: t('roi'),
                    dataIndex: ['follow', 'roi'],
                    key: 'roi',
                    align: 'right' as const,
                    render: (roi: number) => (
                      <OrderHistoryStyles.PnlText $positive={(roi || 0) >= 0}>
                        {roi
                          ? `${roi >= 0 ? '+' : ''}${BigNumber(roi).toFixed(2)}%`
                          : '0.00%'}
                      </OrderHistoryStyles.PnlText>
                    ),
                  },
                  {
                    title: t('manage'),
                    key: 'manage',
                    align: 'right' as const,
                    render: (
                      _: unknown,
                      record: {
                        follow: CopyFollow;
                        portfolio: CopyPortfolio | null;
                      }
                    ) => (
                      <OrderHistoryStyles.FollowingManageButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFollow(record.follow);
                          setSelectedFollowPortfolio(record.portfolio);
                          setOpenEditFollowModal(true);
                        }}
                      >
                        {t('manage')}
                      </OrderHistoryStyles.FollowingManageButton>
                    ),
                  },
                ]}
                dataSource={myFollows || []}
                rowKey={(record) => record.follow.id.toString()}
                pagination={false}
                locale={{
                  emptyText: <TableEmptyState message={t('startExploring')} />,
                }}
              />
            </OrderHistoryStyles.FollowingTableContainer>
          </S.PortfoliosSection>
        </S.FollowingSection>
      ) : activeTab === 'orders' ? (
        <OrderHistoryStyles.OrderHistorySection>
          <OrderHistoryStyles.OrderHistoryHeader>
            <OrderHistoryStyles.OrderHistoryTitle>
              {t('orderHistory')}
            </OrderHistoryStyles.OrderHistoryTitle>
            <OrderHistoryStyles.TotalOrdersCard>
              <OrderHistoryStyles.TotalOrdersLabel>
                {t('totalOrders')}
              </OrderHistoryStyles.TotalOrdersLabel>
              <OrderHistoryStyles.TotalOrdersValue>
                <OrderHistoryStyles.TotalOrdersNumber>
                  {totalOrders.toFixed(2)}
                </OrderHistoryStyles.TotalOrdersNumber>
                <OrderHistoryStyles.RefreshButton onClick={() => fetchOrders()}>
                  <OrderHistoryStyles.RefreshIcon
                    src="/assets/newHome/copy-trading/redo-icon.svg"
                    alt="Refresh"
                  />
                  <OrderHistoryStyles.RefreshText>
                    {t('refresh')}
                  </OrderHistoryStyles.RefreshText>
                </OrderHistoryStyles.RefreshButton>
              </OrderHistoryStyles.TotalOrdersValue>
            </OrderHistoryStyles.TotalOrdersCard>
          </OrderHistoryStyles.OrderHistoryHeader>

          <OrderHistoryStyles.OrdersTableSection>
            <OrderHistoryStyles.FiltersBar>
              <OrderHistoryStyles.FiltersLeft>
                <SelectDropdownGlobalStyle />
                <CSelect
                  value={selectedSymbol}
                  onChange={(value) => {
                    setSelectedSymbol(value as string);
                    setOrdersPage(1);
                  }}
                  options={getSymbolOptions(t)}
                  style={{ width: 180, minWidth: 180 }}
                />
                <CSelect
                  value={selectedStatus}
                  onChange={(value) => {
                    setSelectedStatus(value as string);
                    setOrdersPage(1);
                  }}
                  options={getStatusOptions(t)}
                  style={{ width: 150, minWidth: 150 }}
                />
              </OrderHistoryStyles.FiltersLeft>
              <OrderHistoryStyles.FiltersRight>
                <OrderHistoryStyles.ReloadButton onClick={() => fetchOrders()}>
                  <OrderHistoryStyles.ReloadIcon
                    src="/assets/newHome/copy-trading/reload-icon.svg"
                    alt="Reload"
                  />
                </OrderHistoryStyles.ReloadButton>
              </OrderHistoryStyles.FiltersRight>
            </OrderHistoryStyles.FiltersBar>

            <OrderHistoryStyles.OrderTableContainer>
              <OrderHistoryStyles.OrderTableHeader>
                <h3>{t('orderHistory')}</h3>
                <OrderHistoryStyles.SubText>
                  {t('totalRecords')}: {totalOrders}
                </OrderHistoryStyles.SubText>
              </OrderHistoryStyles.OrderTableHeader>

              <Table<CopyOrder>
                columns={[
                  {
                    title: t('time'),
                    dataIndex: 'createdAt',
                    key: 'createdAt',
                    align: 'center',
                    width: 180,
                    render: (text: string) =>
                      text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-',
                  },
                  {
                    title: t('symbol'),
                    dataIndex: 'symbol',
                    key: 'symbol',
                    align: 'center',
                    width: 120,
                    render: (text: string) => text || '-',
                  },
                  {
                    title: t('side'),
                    dataIndex: 'longShort',
                    key: 'longShort',
                    align: 'center',
                    width: 80,
                    render: (value: string) => (
                      <OrderHistoryStyles.DirectionTag $isLong={value === 'L'}>
                        {value === 'L'
                          ? t('long')
                          : value === 'S'
                            ? t('short')
                            : '-'}
                      </OrderHistoryStyles.DirectionTag>
                    ),
                  },
                  {
                    title: t('type'),
                    dataIndex: 'limitMarket',
                    key: 'limitMarket',
                    align: 'center',
                    width: 80,
                    render: (value: string) =>
                      value === 'L'
                        ? t('limit')
                        : value === 'M'
                          ? t('market')
                          : '-',
                  },
                  {
                    title: t('quantity'),
                    dataIndex: 'quantity',
                    key: 'quantity',
                    align: 'center',
                    width: 120,
                    render: (value: string) =>
                      value ? BigNumber(value).toFixed(4) : '-',
                  },
                  {
                    title: t('price'),
                    dataIndex: 'price',
                    key: 'price',
                    align: 'right',
                    width: 120,
                    render: (value: string) =>
                      value ? BigNumber(value).toFixed(2) : '-',
                  },
                  {
                    title: t('leverage'),
                    dataIndex: 'leverage',
                    key: 'leverage',
                    align: 'right',
                    width: 80,
                    render: (value: number) => (value ? `${value}x` : '-'),
                  },
                  {
                    title: t('status'),
                    dataIndex: 'status',
                    key: 'status',
                    align: 'center',
                    width: 100,
                    render: (status: number) => (
                      <OrderHistoryStyles.OrderStatusTag $status={status}>
                        {status === 0
                          ? t('pending')
                          : status === 1
                            ? t('filled')
                            : t('cancelled')}
                      </OrderHistoryStyles.OrderStatusTag>
                    ),
                  },
                ]}
                dataSource={orders}
                rowKey={(record) => record.id}
                scroll={{ x: 'max-content' }}
                loading={ordersLoading}
                locale={{
                  emptyText: <TableEmptyState message={t('noOrdersFound')} />,
                }}
                pagination={false}
              />

              <Pagination
                currentPage={ordersPage}
                totalPages={Math.ceil(totalOrders / 20)}
                onPageChange={(newPage) => setOrdersPage(newPage)}
                variant="solid"
                align="center"
              />
            </OrderHistoryStyles.OrderTableContainer>
          </OrderHistoryStyles.OrdersTableSection>
        </OrderHistoryStyles.OrderHistorySection>
      ) : activeTab === 'my-portfolio' ? (
        /* My Portfolio Section - Leader Dashboard */
        <MyPortfolioSection isMobile={false} />
      ) : activeTab === 'followers' ? (
        /* Followers Section - Leader's Followers Management */
        <FollowersSection isMobile={false} />
      ) : activeTab === 'earnings' ? (
        /* Earnings Section - Earnings & Settlements */
        <EarningsSection isMobile={false} />
      ) : (
        /* Discover Section - Default */
        <S.TradersSectionWrapper>
          <S.TradersSection>
            <S.TradersHeader>
              <S.TradersTitle>{t('allTraders')}</S.TradersTitle>
              <S.SearchContainer>
                <S.SearchIcon
                  src="/assets/newHome/copy-trading/search-icon.svg"
                  alt="Search"
                />
                <S.SearchInput
                  placeholder={t('searchTraders')}
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                />
              </S.SearchContainer>
            </S.TradersHeader>

            {isLoading ? (
              <S.LoadingContainer>{t('loading')}</S.LoadingContainer>
            ) : (
              <S.TradersGrid>
                {(displayedPortfolios ?? []).map((portfolio, index) => {
                  if (!portfolio) return null;
                  const following = isFollowing(portfolio.id);
                  const myPortfolio = isMyPortfolio(portfolio);
                  const rank = index + 1;

                  return (
                    <S.TraderCard
                      key={portfolio.id}
                      $isMyPortfolio={myPortfolio}
                      $rank={rank}
                      onClick={() =>
                        navigate(`/copy-trade/portfolio/${portfolio.id}`)
                      }
                    >
                      {myPortfolio && (
                        <S.MyPortfolioBadge>
                          {t('myPortfolio')}
                        </S.MyPortfolioBadge>
                      )}
                      <S.CardHeader>
                        <S.TraderInfo>
                          <S.AvatarWrapper>
                            <S.AvatarImage
                              src={
                                getPortfolioImageUrl(portfolio) ||
                                DefaultProfileImage
                              }
                              alt={portfolio.leaderUsername}
                              onError={(e) => {
                                (e.target as HTMLImageElement).src =
                                  DefaultProfileImage;
                              }}
                            />
                            <S.RankBadge $rank={rank}>
                              <S.RankNumber>{rank}</S.RankNumber>
                            </S.RankBadge>
                          </S.AvatarWrapper>
                          <S.TraderDetails>
                            <S.TraderName>
                              {portfolio.leaderUsername || t('unknown')}
                            </S.TraderName>
                            <S.CopyTradesCount>
                              {portfolio.currentFollowers || 0}{' '}
                              {t('copyTrades')}
                            </S.CopyTradesCount>
                          </S.TraderDetails>
                        </S.TraderInfo>
                        {rank <= 3 && (
                          <S.CrownIcon
                            src={`/assets/newHome/copy-trading/crown-rank${rank}.svg`}
                            alt={`Rank ${rank}`}
                          />
                        )}
                      </S.CardHeader>

                      <S.CardStats>
                        <S.ROISection>
                          <S.ROILabel>{t('sevenDayRoi')}</S.ROILabel>
                          <S.ROIValue
                            $isNegative={
                              (portfolio.roi ?? portfolio.roi7d ?? 0) < 0
                            }
                          >
                            {/* ⭐ 7일 ROI 표시 (period=7d로 요청 시 roi 또는 roi7d에 매핑됨) */}
                            {(portfolio.roi !== undefined
                              ? portfolio.roi
                              : portfolio.roi7d) !== undefined
                              ? `${(portfolio.roi ?? portfolio.roi7d ?? 0) >= 0 ? '+' : ''}${(portfolio.roi ?? portfolio.roi7d ?? 0).toFixed(2)}%`
                              : '0.00%'}
                          </S.ROIValue>
                        </S.ROISection>
                        <S.ChartWrapper>
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                              data={generateChartData(
                                portfolio.roi ?? portfolio.roi7d ?? 0
                              )}
                            >
                              <Line
                                type="monotone"
                                dataKey="value"
                                stroke={
                                  (portfolio.roi ?? portfolio.roi7d ?? 0) >= 0
                                    ? '#07d993'
                                    : '#ef4444'
                                }
                                strokeWidth={2}
                                dot={false}
                                isAnimationActive={false}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </S.ChartWrapper>
                      </S.CardStats>

                      <S.CardMetrics>
                        <S.MetricItem>
                          <S.MetricLabel>{t('sevenDayPnl')}</S.MetricLabel>
                          <S.MetricValue>
                            {/* ⭐ 7일 PnL 표시 (period=7d로 요청 시 periodPnl에 매핑됨, 없으면 totalPnl) */}
                            $
                            {(
                              portfolio.periodPnl !== undefined
                                ? portfolio.periodPnl
                                : portfolio.totalPnl
                            )
                              ? (
                                  portfolio.periodPnl ?? portfolio.totalPnl
                                ).toFixed(2)
                              : '0.00'}
                          </S.MetricValue>
                        </S.MetricItem>
                        <S.MetricItem>
                          <S.MetricLabel>
                            {t('copyTradingPage.followers')}
                          </S.MetricLabel>
                          <S.MetricValue>
                            {portfolio.currentFollowers || 0}
                          </S.MetricValue>
                        </S.MetricItem>
                      </S.CardMetrics>

                      <S.CopyTradeButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyTrade(portfolio);
                        }}
                        disabled={following || myPortfolio}
                        $isFollowing={following}
                        $isMyPortfolio={myPortfolio}
                      >
                        {myPortfolio
                          ? t('myPortfolio')
                          : following
                            ? t('following')
                            : t('copyTrade')}
                      </S.CopyTradeButton>
                    </S.TraderCard>
                  );
                })}
              </S.TradersGrid>
            )}
          </S.TradersSection>
        </S.TradersSectionWrapper>
      )}
    </S.Container>
  );
};

// 공통 빈 상태 컴포넌트
interface TableEmptyStateProps {
  message: string;
}

const TableEmptyState: React.FC<TableEmptyStateProps> = ({ message }) => (
  <S.EmptyState>
    <S.EmptyCharacter>
      <S.SpeechBubble>
        <S.SpeechBubbleImage
          src="/assets/newHome/copy-trading/speech-bubble-union.svg"
          alt="Speech Bubble"
        />
        <S.SpeechBubbleText>{message}</S.SpeechBubbleText>
      </S.SpeechBubble>
      <S.CharacterImg
        src="/assets/newHome/copy-trading/empty-character.png"
        alt="Character"
      />
    </S.EmptyCharacter>
  </S.EmptyState>
);

export default CopyTradingPage;
